import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import Layout from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { clientPortalAPI } from '../../api';
import { useAuthStore } from '../../store/authStore';
import { useToast } from '../../hooks/useToast';
import { Loader2 } from 'lucide-react';

export default function ClientAccount() {
  const { updateUser } = useAuthStore();
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();

  const { data, isLoading } = useQuery({
    queryKey: ['portal-me'],
    queryFn: () => clientPortalAPI.getMe(),
  });

  const [contact, setContact] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    mobile: '',
  });
  const [clientAddr, setClientAddr] = useState({
    companyName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    county: '',
    postcode: '',
    country: 'GB',
  });
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });

  useEffect(() => {
    if (!data) return;
    setContact({
      firstName: data.contact?.firstName || '',
      lastName: data.contact?.lastName || '',
      email: data.contact?.email || '',
      phone: data.contact?.phone || '',
      mobile: data.contact?.mobile || '',
    });
    setClientAddr({
      companyName: data.client?.companyName || '',
      addressLine1: data.client?.addressLine1 || '',
      addressLine2: data.client?.addressLine2 || '',
      city: data.client?.city || '',
      county: data.client?.county || '',
      postcode: data.client?.postcode || '',
      country: data.client?.country || 'GB',
    });
  }, [data]);

  const saveProfile = useMutation({
    mutationFn: () =>
      clientPortalAPI.updateMe({
        contact: {
          firstName: contact.firstName,
          lastName: contact.lastName,
          email: contact.email,
          phone: contact.phone,
          mobile: contact.mobile,
        },
        client: clientAddr,
      }),
    onSuccess: (res) => {
      if (res.user) updateUser(res.user);
      queryClient.invalidateQueries(['portal-me']);
      success('Profile saved');
    },
    onError: (e) => showError(e.response?.data?.error || e.message || 'Save failed'),
  });

  const changePwd = useMutation({
    mutationFn: () =>
      clientPortalAPI.changePassword({
        currentPassword: pwd.current,
        newPassword: pwd.next,
      }),
    onSuccess: () => {
      setPwd({ current: '', next: '', confirm: '' });
      success('Password updated');
    },
    onError: (e) => showError(e.response?.data?.error || e.message || 'Could not update password'),
  });

  const submitPassword = (e) => {
    e.preventDefault();
    if (pwd.next.length < 8) {
      showError('New password must be at least 8 characters');
      return;
    }
    if (pwd.next !== pwd.confirm) {
      showError('New passwords do not match');
      return;
    }
    changePwd.mutate();
  };

  if (isLoading && !data) {
    return (
      <Layout>
        <div className="flex justify-center py-20">
          <Loader2 className="h-10 w-10 animate-spin text-[var(--ds-text-muted)]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)]">
        <div className="max-w-3xl mx-auto px-6 py-6">
          <h1 className="text-display font-bold text-[var(--ds-text-primary)]">My account</h1>
          <p className="text-meta text-[var(--ds-text-secondary)] mt-1">Update your details and password.</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        <Card>
          <CardContent className="p-6 space-y-4">
            <h2 className="text-h2 font-semibold text-[var(--ds-text-primary)]">Contact details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm mb-1">First name</label>
                <input
                  className="w-full rounded-md border border-[var(--ds-border-subtle)] bg-background px-3 py-2 text-sm"
                  value={contact.firstName}
                  onChange={(e) => setContact((c) => ({ ...c, firstName: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Last name</label>
                <input
                  className="w-full rounded-md border border-[var(--ds-border-subtle)] bg-background px-3 py-2 text-sm"
                  value={contact.lastName}
                  onChange={(e) => setContact((c) => ({ ...c, lastName: e.target.value }))}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm mb-1">Email</label>
                <input
                  type="email"
                  className="w-full rounded-md border border-[var(--ds-border-subtle)] bg-background px-3 py-2 text-sm"
                  value={contact.email}
                  onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Phone</label>
                <input
                  className="w-full rounded-md border border-[var(--ds-border-subtle)] bg-background px-3 py-2 text-sm"
                  value={contact.phone}
                  onChange={(e) => setContact((c) => ({ ...c, phone: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Mobile</label>
                <input
                  className="w-full rounded-md border border-[var(--ds-border-subtle)] bg-background px-3 py-2 text-sm"
                  value={contact.mobile}
                  onChange={(e) => setContact((c) => ({ ...c, mobile: e.target.value }))}
                />
              </div>
            </div>

            <h2 className="text-h2 font-semibold text-[var(--ds-text-primary)] pt-4">Billing address</h2>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm mb-1">Company name (if any)</label>
                <input
                  className="w-full rounded-md border border-[var(--ds-border-subtle)] bg-background px-3 py-2 text-sm"
                  value={clientAddr.companyName}
                  onChange={(e) => setClientAddr((c) => ({ ...c, companyName: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Address line 1</label>
                <input
                  className="w-full rounded-md border border-[var(--ds-border-subtle)] bg-background px-3 py-2 text-sm"
                  value={clientAddr.addressLine1}
                  onChange={(e) => setClientAddr((c) => ({ ...c, addressLine1: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Address line 2</label>
                <input
                  className="w-full rounded-md border border-[var(--ds-border-subtle)] bg-background px-3 py-2 text-sm"
                  value={clientAddr.addressLine2}
                  onChange={(e) => setClientAddr((c) => ({ ...c, addressLine2: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm mb-1">City</label>
                  <input
                    className="w-full rounded-md border border-[var(--ds-border-subtle)] bg-background px-3 py-2 text-sm"
                    value={clientAddr.city}
                    onChange={(e) => setClientAddr((c) => ({ ...c, city: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1">County</label>
                  <input
                    className="w-full rounded-md border border-[var(--ds-border-subtle)] bg-background px-3 py-2 text-sm"
                    value={clientAddr.county}
                    onChange={(e) => setClientAddr((c) => ({ ...c, county: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1">Postcode</label>
                  <input
                    className="w-full rounded-md border border-[var(--ds-border-subtle)] bg-background px-3 py-2 text-sm"
                    value={clientAddr.postcode}
                    onChange={(e) => setClientAddr((c) => ({ ...c, postcode: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            <Button type="button" onClick={() => saveProfile.mutate()} disabled={saveProfile.isLoading}>
              {saveProfile.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save profile'}
            </Button>
          </CardContent>
        </Card>

        {data?.passwordIsSet && (
          <Card>
            <CardContent className="p-6 space-y-4">
              <h2 className="text-h2 font-semibold text-[var(--ds-text-primary)]">Change password</h2>
              <form onSubmit={submitPassword} className="space-y-4 max-w-md">
                <div>
                  <label className="block text-sm mb-1">Current password</label>
                  <input
                    type="password"
                    autoComplete="current-password"
                    className="w-full rounded-md border border-[var(--ds-border-subtle)] bg-background px-3 py-2 text-sm"
                    value={pwd.current}
                    onChange={(e) => setPwd((p) => ({ ...p, current: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1">New password</label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    className="w-full rounded-md border border-[var(--ds-border-subtle)] bg-background px-3 py-2 text-sm"
                    value={pwd.next}
                    onChange={(e) => setPwd((p) => ({ ...p, next: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1">Confirm new password</label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    className="w-full rounded-md border border-[var(--ds-border-subtle)] bg-background px-3 py-2 text-sm"
                    value={pwd.confirm}
                    onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))}
                  />
                </div>
                <Button type="submit" disabled={changePwd.isLoading}>
                  {changePwd.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update password'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
